<template>
  <v-card elevation="0">
    <v-card-title>
      {{ label }}
      <v-tooltip v-if="tooltip" bottom max-width="500">
        <template v-slot:activator="{ on, attrs }">
          <v-icon color="grey lighten-1" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
        </template>
        <span>{{ tooltip }}</span>
      </v-tooltip>

      <v-btn v-if="editable" small color="primary" class="ml-auto" @click="$emit('new-role', label)">
        <v-icon>mdi-plus</v-icon>{{ $t('AdminCard.newRole') }}
      </v-btn>
    </v-card-title>
    <v-card-text>
      <v-card outlined>
        <v-list >
          <template v-for="(item, i) in data">
            <v-list-item :key="item.no">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.name }}
                </v-list-item-title>
              </v-list-item-content>

              <v-list-item-action v-if="clinicUser" class="flex-row align-center" style="gap: 8px;">
                <v-btn
                  small
                  :outlined="!item.stop_notify"
                  :color="item.stop_notify ? 'error' : 'success'"
                  @click="openToggleDialog(item)"
                >
                  {{ item.stop_notify ? '拒收中' : '接收中' }}
                </v-btn>
                <v-btn small outlined color="grey darken-1" @click="openSettings(item)">
                  <v-icon small left>mdi-cog-outline</v-icon>
                  訊息設定
                </v-btn>
              </v-list-item-action>

              <v-list-item-action v-if="editable">
                <v-btn icon @click="remove(item)">
                  <v-icon color="red lighten-1">mdi-minus-circle-outline</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider v-if="i !== data.length-1"/>
          </template>
        </v-list>
      </v-card>
    </v-card-text>
    <v-dialog v-if="removeDialog" v-model="removeDialog" max-width="372">
      <v-card>
        <v-card-title v-text="title"/>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="primary" @click="confirm">{{ $t('AdminCard.ok') }}</v-btn>
          <v-btn text @click="removeDialog = false">{{ $t('GENERAL.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="toggleDialog" v-model="toggleDialog" max-width="400">
      <v-card>
        <v-card-title>
          {{ toggleItem && toggleItem.stop_notify ? '解除拒收' : '設為拒收' }}
        </v-card-title>
        <v-card-text>
          <template v-if="toggleItem && toggleItem.stop_notify">
            確定要將 <strong>{{ toggleItem.name }}</strong> 的通知狀態改為「接收」嗎？
          </template>
          <template v-else-if="toggleItem">
            確定要將 <strong>{{ toggleItem.name }}</strong> 的通知狀態改為「拒收」嗎？<br>
            <span class="red--text">拒收後將無法收到任何通知。</span>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="primary" :loading="toggleLoading" :disabled="toggleLoading" @click="confirmToggle">確認</v-btn>
          <v-btn text :disabled="toggleLoading" @click="toggleDialog = false">取消</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="settingsDialog" v-model="settingsDialog" max-width="500">
      <v-card>
        <v-card-title>
          {{ label }}訊息設定
          <v-spacer/>
          <v-btn icon @click="settingsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div v-for="setting in editedSettings" :key="setting.key">
            <v-checkbox
              v-model="setting.status"
              :label="setting.title"
              :disabled="isForceEnabled(setting.key)"
              hide-details
              class="mt-0"
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="primary" :loading="loading" :disabled="loading" @click="saveSettings">確認</v-btn>
          <v-btn text :disabled="loading" @click="settingsDialog = false">取消</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmDialog
      v-model="blockDialog"
      title="無法調整訊息設定"
      content="此成員目前為拒收狀態，請先解除拒收後才能調整訊息設定。"
      confirm-btn-text="我知道了"
      :cancel-btn-text="null"
      @confirm="({ callback }) => callback()"
    />
  </v-card>
</template>

<script>
import { mapGetters } from "vuex"
import { updateAdminAPI } from "@/api"
import ConfirmDialog from "@/components/utils/ConfirmDialog.vue"

export default {
  name: "AdminCard",
  components: {
    ConfirmDialog
  },
  props: {
    data: Array,
    label: String,
    tooltip: String,
    editable: Boolean,
  },
  data() {
    return {
      removeDialog: false,
      title: null,
      removeItem: null,
      settingsDialog: false,
      currentItem: null,
      editedSettings: [],
      loading: false,
      toggleDialog: false,
      toggleItem: null,
      toggleLoading: false,
      blockDialog: false,
    }
  },
  computed: {
    ...mapGetters(['clinicUser', 'axiosParamData'])
  },
  methods: {
    remove(item) {
      this.title = this.$t('AdminCard.removeTitle', { name: item.name, label: this.label })
      this.removeItem = item
      this.removeDialog = true
    },
    confirm() {
      this.$emit('delete', this.removeItem.no, this.removeItem.line_bot_id)
      this.removeDialog = false
    },
    openToggleDialog(item) {
      this.toggleItem = item
      this.toggleDialog = true
    },
    confirmToggle() {
      this.toggleLoading = true
      const data = {
        ...this.axiosParamData,
        no: this.toggleItem.no,
        stop_notify: !this.toggleItem.stop_notify
      }
      updateAdminAPI(data).then(res => {
        this.toggleDialog = false
        this.$emit('refresh')
      }).catch(e => {
        if (e.response && e.response.data && e.response.data.messages) {
          alert(e.response.data.messages)
        } else {
          alert(this.$t('GENERAL.oops'))
          console.error(e)
        }
      }).finally(() => {
        this.toggleLoading = false
      })
    },
    openSettings(item) {
      if (item.stop_notify) {
        this.blockDialog = true
        return
      }
      this.currentItem = item
      this.editedSettings = JSON.parse(JSON.stringify(item.remind_settings))
      this.settingsDialog = true
    },
    isForceEnabled(key) {
      return key === 'clinic_reports_weekly' || key === 'doctor_reports_weekly'
    },
    saveSettings() {
      this.loading = true
      const data = {
        ...this.axiosParamData,
        no: this.currentItem.no,
        remind_settings: this.editedSettings
      }
      updateAdminAPI(data).then(res => {
        this.settingsDialog = false
        this.$emit('refresh')
      }).catch(e => {
        if (e.response && e.response.data && e.response.data.messages) {
          alert(e.response.data.messages)
        } else {
          alert(this.$t('GENERAL.oops'))
          console.error(e)
        }
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>

</style>